import React, { useState, useEffect } from 'react';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useToast } from '../../../context/ToastContext';
import { AdminService } from '../../../application/services/AdminService';
import { Loader2 } from 'lucide-react';

const MovimientoForm = ({ onClose, onSuccess, initialData }) => {
    const toast = useToast();
    const isEditing = !!initialData;
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        tipo: 'Ingreso',
        descripcion: '',
        cantidad: '',
        precioUnitario: '',
        precioTotal: 0,
        fecha: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        if (initialData) {
            setFormData({
                tipo: initialData.tipo || 'Ingreso',
                descripcion: initialData.descripcion || '',
                cantidad: initialData.cantidad || '',
                precioUnitario: initialData.precioUnitario || '',
                precioTotal: initialData.precioTotal || 0,
                fecha: initialData.fecha ? initialData.fecha.split('T')[0] : new Date().toISOString().split('T')[0]
            });
        }
    }, [initialData]);

    // Calcular precio total automáticamente
    useEffect(() => {
        const cantidad = parseFloat(formData.cantidad) || 0;
        const precioUnitario = parseFloat(formData.precioUnitario) || 0;
        setFormData(prev => ({ ...prev, precioTotal: cantidad * precioUnitario }));
    }, [formData.cantidad, formData.precioUnitario]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.tipo) newErrors.tipo = 'Seleccione un tipo';
        if (!formData.descripcion.trim()) newErrors.descripcion = 'Descripción requerida';
        if (!formData.cantidad || parseFloat(formData.cantidad) <= 0) newErrors.cantidad = 'Cantidad debe ser mayor a 0';
        if (!formData.precioUnitario || parseFloat(formData.precioUnitario) < 0) newErrors.precioUnitario = 'Precio inválido';
        if (!formData.fecha) newErrors.fecha = 'Fecha requerida';
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const data = {
                tipo: formData.tipo,
                descripcion: formData.descripcion.trim(),
                cantidad: parseFloat(formData.cantidad),
                precioUnitario: parseFloat(formData.precioUnitario),
                precioTotal: formData.precioTotal,
                fecha: formData.fecha
            };

            if (isEditing) {
                await AdminService.updateMovimiento(initialData.id, data);
                toast.success('Registro actualizado exitosamente');
            } else {
                await AdminService.createMovimiento(data);
                toast.success('Registro creado exitosamente');
            }
            onSuccess();
        } catch (error) {
            console.error('Error saving movimiento:', error);
            toast.error(error || 'Error al guardar el registro');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(value || 0);
    };

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={isEditing ? 'Editar Registro' : 'Nuevo Registro'}
            size="xl"
            footer={
                <div className="flex w-full justify-between items-center">
                    <div className="text-lg font-semibold text-gray-700">
                        Total: <span className="text-blue-600">{formatCurrency(formData.precioTotal)}</span>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={onClose} disabled={loading}>
                            Cancelar
                        </Button>
                        <Button onClick={handleSubmit} disabled={loading}>
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    Guardando...
                                </>
                            ) : (
                                isEditing ? 'Guardar Cambios' : 'Registrar'
                            )}
                        </Button>
                    </div>
                </div>
            }
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Tipo */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                        <select
                            name="tipo"
                            value={formData.tipo}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="Ingreso">Ingreso</option>
                            <option value="Egreso">Egreso</option>
                        </select>
                        {errors.tipo && <p className="mt-1 text-sm text-red-600">{errors.tipo}</p>}
                    </div>
                </div>

                {/* Descripción */}
                <Input 
                    label="Descripción"
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    placeholder="Ej: Pago de servicios básicos"
                    error={errors.descripcion}
                />

                {/* Cantidad y Precio Unitario en grid */}
                <div className="grid grid-cols-2 gap-4">
                    <Input 
                        label="Cantidad"
                        name="cantidad"
                        type="number"
                        min="1"
                        step="1"
                        value={formData.cantidad}
                        onChange={handleChange}
                        placeholder="Ej: 1"
                        error={errors.cantidad}
                    />
                    <Input 
                        label="Precio Unitario ($)"
                        name="precioUnitario"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.precioUnitario}
                        onChange={handleChange}
                        placeholder="Ej: 25.50"
                        error={errors.precioUnitario}
                    />
                </div>

                {/* Fecha */}
                <Input 
                    label="Fecha"
                    name="fecha"
                    type="date"
                    value={formData.fecha}
                    onChange={handleChange}
                    error={errors.fecha}
                />
            </form>
        </Modal>
    );
};

export default MovimientoForm;
